import { Avatar, Flex, Spacer, Text } from '@chakra-ui/react'
import { useWorkspace } from 'components/providers'
import { PermissionValue } from 'types'
import { useUsers } from 'utils'
import { CheckBox, CheckIcon, MenuProps } from '.'
import Menu from './Menu'

interface Props<T extends boolean> extends Omit<MenuProps, 'value' | 'onChange'> {
  isMulti?: T
  onChange: (userId: T extends true ? string[] : string) => void
  permissionsAllowed?: Array<PermissionValue>
  userDetail?: Record<string, Detail>
  value: T extends true ? string[] : string
}

interface Detail {
  hideFromValue?: boolean
  title?: string
}

export default function UserSelect<T extends boolean = false>({
  isMulti,
  onChange,
  permissionsAllowed,
  userDetail,
  value,
  ...rest
}: Props<T>) {
  const { workspace } = useWorkspace()

  const userById = workspace?.userById ?? {}
  let filteredUserIds = Object.keys(userById)
  if (permissionsAllowed) {
    filteredUserIds = filteredUserIds.filter(
      userId => userId && permissionsAllowed!.includes(userById[userId]?.permission)
    )
  }
  const { data: users } = useUsers(filteredUserIds)

  const selectedUsers = users
    ? typeof value === 'string'
      ? [users.find(u => u?.id === value)]
      : value.map(v => users.find(u => u?.id === v))
    : undefined
  const firstUser = selectedUsers?.[0]
  const firstThreeUsers = selectedUsers
    ?.filter(u => u?.id && !userDetail?.[u?.id]?.hideFromValue)
    ?.slice(0, 3)

  function handleChange(id: string) {
    if (!isMulti) return onChange(id as any)

    let selectedIds = value as string[]
    if (!selectedIds.some(v => v === id)) selectedIds.push(id)
    else selectedIds = selectedIds.filter(v => v !== id)
    onChange(selectedIds as any)
  }

  return (
    <Menu
      width="100%"
      variant="outline"
      borderRadius="xs"
      menuProps={{ closeOnSelect: !isMulti, matchWidth: true }}
      noPortal
      buttonLabel={
        value.length && firstUser ? (
          <Flex align="center" gap={2}>
            <Flex gap={2} flexWrap="wrap">
              {isMulti ? (
                <Flex align="center" gap={2} overflow="hidden">
                  <Flex>
                    {firstThreeUsers?.map((user, index) => (
                      <Avatar
                        key={user?.id}
                        ml={index && '-10px'}
                        src={user?.photoUrl}
                        name={user?.name}
                        size="sm"
                      />
                    ))}
                    {selectedUsers.length > 3 && (
                      <Flex
                        align="center"
                        bg="gray.400"
                        borderRadius="full"
                        color="white"
                        height="32px"
                        justify="center"
                        ml="-10px"
                        width="32px"
                        zIndex={1}
                      >
                        +{selectedUsers.length - 3}
                      </Flex>
                    )}
                  </Flex>
                  <Text textStyle="bodySmall" fontWeight="500">
                    {selectedUsers?.length} Members
                  </Text>
                </Flex>
              ) : (
                <Flex align="center" gap={2} overflow="hidden">
                  <Avatar src={firstUser?.photoUrl} name={firstUser?.name} size="sm" />
                  <Text textStyle="bodySmall" fontWeight="500">
                    {firstUser?.name}
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        ) : (
          <Text textStyle="bodySmall" fontWeight="500">
            Select...
          </Text>
        )
      }
      options={[
        {
          type: 'group',
          options:
            users?.map(user => {
              const { displayName, id = '', photoUrl } = user || {}
              const { title } = userDetail?.[id] || {}

              return {
                value: id,
                label: (
                  <Flex align="center" flex={1} gap={2}>
                    {isMulti ? (
                      <CheckBox isChecked={value.includes(id)} />
                    ) : (
                      <CheckIcon fontSize="medium" fill={value === id ? 'black' : 'transparent'} />
                    )}
                    <Avatar name={displayName} src={photoUrl} size="sm" />
                    <Text textStyle="bodySmall" fontWeight="500">
                      {displayName}
                    </Text>
                    {title && (
                      <>
                        <Spacer />
                        <Text textStyle="bodySmall" color="gray.500">
                          {title}
                        </Text>
                      </>
                    )}
                  </Flex>
                ),
              }
            }) ?? [],
        },
      ]}
      onChange={handleChange}
      {...rest}
    />
  )
}
